import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Theme, TextField, Grid, Box, Collapse } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
	updateOrderProductsDuration,
	updateOrderProductsPricing,
	updateOrderProductsDates,
	getStartDateWithInventoryBlockers,
	getEndDateWithInventoryBlockers,
} from 'common/utils/productUtils';
import {
	getTotalRentalDurationFromProducts,
	getEarliestProductStartTime,
	getNextProductReturnTime,
	getLatestProductEndTime,
	dateIsOutsideOpeningHours,
} from 'common/utils/dateCalculations';
import { getDurationTypeFromTimePeriod } from 'common/utils/pricing';
import {
	Shopper,
	OrderProduct,
	Duration,
	DurationWithPrice,
	OrderInfo,
	UserDetailName,
	ShopPublicInfo,
	UserProperty,
	PartialWithId,
	PurchaseTypes,
} from 'common/types';
import {
	getDurationOptionsForOrderProducts,
	getSelectedDurationOptionIndex,
} from 'common/utils/pricing';
import { RentleSegment, getRentleSegments } from 'common/utils/segments';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { useSelector, useDispatch } from 'react-redux';
import { calculatePriceDifferenceForSegment, getUpdatedProductDinValues } from '../utils';
import {
	getShopperNameKeywords,
	getShopperNames,
	getStockProductIdsFromProducts,
	getCategoryIdsFromShoppers,
	getShopperIdsWithProductsFromShoppers,
	getUniqueProducts,
} from 'common/utils/rentalUtils';
import { updateActivePartialOrder } from 'actions';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import DateAndTimeSelector from 'components/DateAndTimeSelector';
import HasFeature from 'components/features/HasFeature';
import { useTranslation } from 'services/localization/useTranslation';
import UserDetailField from 'common/components/UserDetailField';
import { getSortedRequiredDetails } from 'common/utils/userDetails';
import { EditHeaderBar } from './components/EditHeaderBar';
import { SegmentSelect } from './components/SegmentSelect';
import { getOrderPricing, getPricingOptions, getTotalProductPricing } from 'common/modules/orders';
import { orderHasPickup } from 'common/modules/delivery/utils';
import { usePermissions } from 'hooks/usePermissions';
import OverbookedBanner from 'components/OverbookedBanner';
import DurationDropdownWithAvailability from 'components/DurationDropdownWithAvailability';
import { LoadingData } from 'services/types';
import {
	ProductTotalAvailability,
	getOverbookedVariantIdsFromProductTotalAvailabilities,
} from 'common/api/frontend/inventory_new';
import { getNewEndTime } from 'common/utils/dateCalculations';
import { TimeSelectorArgs } from 'components/TimeSelector/types';
import { handleQuantityAndAvailabilityFetch } from 'common/api/frontend/inventory_new/getQuantitiesAndAvailabilities';
import { useHasFeature } from 'hooks';
import { notUndefined } from 'common/utils/common';

interface Props {
	shopper: Shopper;
	endEditing: () => void;
}

const segments = getRentleSegments();

const EditView = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();
	const { hasFeature } = useHasFeature();

	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const stockProductsMap = useSelector(StockSelectors.stockProductsById);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const shopUnitSystem = useSelector(ShopSelectors.getShopUnitSystem);
	const openingHoursWithExceptions = useSelector(RentalSelectors.openingHoursWithExceptions);
	const shopDinCollectionMethod = useSelector(ShopSelectors.shopDinCollectionMethod);

	const order = useSelector(RentalSelectors.activeOrder)!;
	const products = useSelector(RentalSelectors.orderProducts);
	const rentalInfo = useSelector(RentalSelectors.orderInfo)!;
	const orderDelivery = useSelector(RentalSelectors.orderDelivery);
	const shoppers = useSelector(RentalSelectors.orderShoppers);
	const savedPricingTables = shopPublicInfo.pricingTables || {};

	const { shopper } = props;
	const shopperId = shopper.id;
	const ignoredOrderProductIds = useMemo(() => products && products.map((product) => product.id), [
		products,
	]);
	const shopperProducts = useMemo(() => products.filter((p) => p.shopperId === shopperId), [
		products,
		shopperId,
	]);
	const currency = getCurrencyObjectFromCurrencyCode(rentalInfo.pricing.currency, shopCurrency);
	const details = getSortedRequiredDetails(shopperProducts);
	const shopperEndDate = getLatestProductEndTime(shopperProducts) || rentalInfo.endDateReturned;

	const rentalHasPickup = orderHasPickup(order?.orderDelivery);
	const shopperHasRentals = shopperProducts.some((p) => p.purchaseType === PurchaseTypes.rental);

	const [newRentalDuration, setNewRentalDuration] = useState<Duration | undefined>(undefined);
	const [newSegment, setNewSegment] = useState<RentleSegment | string>(shopper.segment ?? '');
	const [newFirstName, setNewFirstName] = useState<string>(shopper.firstName);
	const [newLastName, setNewLastName] = useState<string>(shopper.lastName);
	const [newUserProperties, setNewUserProperties] = useState(shopper.userProperties);
	const [
		updatedShopperProductsOnSegmentChange,
		setUpdatedShopperProductsOnSegmentChange,
	] = useState<OrderProduct[]>([]);
	const [newEndDate, setNewEndDate] = useState<string | null>(shopperEndDate);
	const [productAvailabilities, setProductAvailabilities] = useState<
		LoadingData<ProductTotalAvailability[] | null>
	>({ data: null, loading: false, error: null });

	const [durationProductAvailabilities, setDurationProductAvailabilities] = useState<
		LoadingData<ProductTotalAvailability[] | null>
	>({ data: null, loading: false, error: null });
	const [timeEditHasHappened, setTimeEditHasHappened] = useState<boolean>(false);

	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');

	const overbookedVariantIds = useMemo(
		() =>
			(productAvailabilities.data &&
				getOverbookedVariantIdsFromProductTotalAvailabilities(
					productAvailabilities.data,
					shopperProducts,
				)) ??
			[],
		[productAvailabilities.data, shopperProducts],
	);
	const hasOverbookedVariants = !!overbookedVariantIds.length;
	const to = moment(newEndDate);
	const isOutsideOpeningHours = dateIsOutsideOpeningHours(to, openingHoursWithExceptions);
	const showBanner = timeEditHasHappened && (hasOverbookedVariants || isOutsideOpeningHours);
	const useAvailabilities = !hasFeature('DISABLE_PRODUCT_AVAILABILITY_LOADING', { scope: 'shop' });

	const rentalDuration = getTotalRentalDurationFromProducts(shopperProducts);

	const isFormError = useMemo(() => {
		for (const detail in newUserProperties) {
			if (!newUserProperties[detail].value) {
				return true;
			}
		}
		return false;
	}, [newUserProperties]);

	const selectorType = useMemo(
		(): TimeSelectorArgs => ({
			type: 'endTime',
			startTime: moment(rentalInfo.startDate),
		}),
		[rentalInfo.startDate],
	);

	const segmentOptions = segments.map((segment) => ({
		value: segment,
		priceDifference: calculatePriceDifferenceForSegment(
			segment,
			shopperProducts,
			stockProducts,
			savedPricingTables,
		),
	}));

	const loadProductAvailabilities = useCallback(
		async (
			start: moment.Moment,
			end: moment.Moment,
			products: OrderProduct[],
			ignoredOrderProductIds?: string[],
		) => {
			return await Promise.all(
				getUniqueProducts(products, stockProductsMap).map((product) => {
					const startDateWithInventoryBlockers = getStartDateWithInventoryBlockers({
						startDate: start.toISOString(),
					});
					const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
						end.toISOString(),
						product,
						stockProducts,
					);
					return handleQuantityAndAvailabilityFetch({
						product,
						stockProducts,
						startDate: startDateWithInventoryBlockers,
						endDate: endDateWithInventoryBlockers,
						shopId: shopPublicInfo.shopId,
						startLocationId: rentalInfo.startLocation.id,
						shopLocations,
						ignoredOrderProductIds,
						purchaseType: 'rental',
					});
				}),
			);
		},
		[
			rentalInfo.startLocation.id,
			shopLocations,
			shopPublicInfo.shopId,
			stockProducts,
			stockProductsMap,
		],
	);

	useEffect(() => {
		if (!shopperHasRentals) {
			return;
		}

		const fetchDurationProductAvailabilities = async (
			start: moment.Moment,
			end: moment.Moment,
			products: OrderProduct[],
			ignoredOrderProductIds?: string[],
		) => {
			setDurationProductAvailabilities({ data: null, error: null, loading: true });
			const loadDurationProductAvailabilities = await loadProductAvailabilities(
				start,
				end,
				products,
				ignoredOrderProductIds,
			);
			setDurationProductAvailabilities({
				data: loadDurationProductAvailabilities,
				error: null,
				loading: false,
			});
		};
		const productsForShopper = updatedShopperProductsOnSegmentChange.length
			? updatedShopperProductsOnSegmentChange
			: shopperProducts;

		const productsNotYetReturned = productsForShopper.filter((product) => !product.endDateReturned);
		const segment = newSegment ? newSegment : shopper.segment;
		const durationOptions = getDurationOptionsForOrderProducts(
			productsNotYetReturned,
			stockProducts,
			shopPublicInfo.pricingTables || {},
			openingHoursWithExceptions,
			rentalInfo.startDate,
			'ADMIN',
			segment,
		);
		const lastDurationWithPrice = durationOptions[durationOptions.length - 1];
		const durationType = getDurationTypeFromTimePeriod(lastDurationWithPrice.timePeriod);
		const durationName = lastDurationWithPrice.label ?? null;

		const longestDuration = {
			...lastDurationWithPrice,
			durationType,
			durationName,
		};

		const startDate = moment(rentalInfo.startDate);

		const endDate = moment(
			getNewEndTime(
				startDate.toISOString(),
				longestDuration,
				openingHoursWithExceptions,
				shopTimeZone,
			),
		);

		const ignoredOrderProductIds = productsNotYetReturned.map((p) => p.id);
		if (useAvailabilities) {
			fetchDurationProductAvailabilities(
				startDate,
				endDate,
				productsNotYetReturned,
				ignoredOrderProductIds,
			);
		}
	}, [
		newSegment,
		shopPublicInfo.pricingTables,
		shopper.segment,
		shopperProducts,
		openingHoursWithExceptions,
		updatedShopperProductsOnSegmentChange,
		rentalInfo.startDate,
		rentalInfo.startLocation.id,
		shopLocations,
		shopTimeZone,
		shopPublicInfo.shopId,
		stockProducts,
		stockProductsMap,
		loadProductAvailabilities,
		useAvailabilities,
		shopperHasRentals,
	]);

	useEffect(() => {
		if (!shopperHasRentals) {
			return;
		}
		const fetchAvailabilities = async (
			start: moment.Moment,
			end: moment.Moment,
			products: OrderProduct[],
			ignoredOrderProductIds?: string[],
		) => {
			setProductAvailabilities({ data: null, error: null, loading: true });
			const productAvailabilities = await loadProductAvailabilities(
				start,
				end,
				products,
				ignoredOrderProductIds,
			);
			setProductAvailabilities({ data: productAvailabilities, error: null, loading: false });
		};
		const bookingStartDate = moment(rentalInfo.startDate);
		const endDate = moment(newEndDate);
		const availabilityStartDate =
			bookingStartDate.isAfter(moment()) || endDate.isBefore(moment())
				? bookingStartDate
				: moment();

		if (useAvailabilities) {
			fetchAvailabilities(availabilityStartDate, endDate, products, ignoredOrderProductIds);
		}
	}, [
		products,
		stockProducts,
		stockProductsMap,
		ignoredOrderProductIds,
		rentalInfo,
		newEndDate,
		shopLocations,
		shopPublicInfo.shopId,
		loadProductAvailabilities,
		useAvailabilities,
		shopperHasRentals,
	]);

	useEffect(() => {
		setNewFirstName(shopper.firstName);
	}, [shopper.firstName]);

	useEffect(() => {
		setNewLastName(shopper.lastName);
	}, [shopper.lastName]);

	useEffect(() => {
		setNewUserProperties(shopper.userProperties);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(shopper.userProperties)]);

	useEffect(() => {
		setNewRentalDuration(rentalDuration);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(rentalDuration)]);

	useEffect(() => {
		if (shopper.segment) {
			setNewSegment(shopper.segment as RentleSegment);
		} else {
			setNewSegment('');
		}
	}, [shopper.segment]);

	const handleRentalDurationChange = (selectedDurationOption: DurationWithPrice | null) => {
		if (!selectedDurationOption) {
			return;
		}
		const newDurationInSeconds = selectedDurationOption.durationInSeconds;
		const newDurationType = getDurationTypeFromTimePeriod(selectedDurationOption.timePeriod);
		const newDurationName = selectedDurationOption.label || null;
		// if active state rented out, calculate new end time for rental.
		setNewRentalDuration({
			durationInSeconds: newDurationInSeconds,
			durationType: newDurationType,
			durationName: newDurationName,
		});
		const startDate = getEarliestProductStartTime(shopperProducts);
		if (startDate) {
			const endDate = moment(startDate).add(newDurationInSeconds, 'seconds').toISOString();
			setNewEndDate(endDate);
		}
		setTimeEditHasHappened(true);
	};

	const handleUndoEditingShopper = () => {
		setNewFirstName(shopper.firstName);
		setNewLastName(shopper.lastName);
		setNewSegment(shopper.segment || ('' as any));
		setNewUserProperties(shopper.userProperties);
		setNewRentalDuration(rentalDuration);
		props.endEditing();
		setUpdatedShopperProductsOnSegmentChange([]);
	};

	const getUpdatedShoppers = () => {
		return shoppers.map((s) => {
			if (s.id === props.shopper.id) {
				return {
					...props.shopper,
					firstName: newFirstName,
					lastName: newLastName,
					segment: newSegment,
					userProperties: newUserProperties,
				};
			} else {
				return s;
			}
		});
	};

	const handleNewFirstNameChange = (event: any) => {
		setNewFirstName(event.target.value);
	};

	const handleNewLastNameChange = (event: any) => {
		setNewLastName(event.target.value);
	};

	const handleNewSegmentChange = (segment: RentleSegment | string) => {
		setNewSegment(segment);
		if (segment !== shopper.segment) {
			const updatedShopperProducts = updateOrderProductsPricing(
				shopperProducts,
				stockProducts,
				savedPricingTables,
				'ADMIN',
				segment,
			);
			setUpdatedShopperProductsOnSegmentChange(updatedShopperProducts);
		} else {
			setUpdatedShopperProductsOnSegmentChange([]);
		}
	};

	const handleNewUserPropertiesChange = (value: UserProperty | null, detail: UserDetailName) => {
		if (!value) {
			const { [detail]: _removedProperty, ...otherProperties } = newUserProperties;
			setNewUserProperties({ ...otherProperties });
		} else {
			setNewUserProperties({ ...newUserProperties, [detail]: value });
		}
	};

	const handleSaveShopperChanges = () => {
		const updatedEndDate =
			newEndDate && !isEqual(newEndDate, shopperEndDate) ? newEndDate : undefined;
		const updatedDuration =
			newRentalDuration && !isEqual(newRentalDuration, rentalDuration)
				? newRentalDuration
				: undefined;
		// If only shopper info changes
		if (updatedShopperProductsOnSegmentChange.length === 0 && !updatedEndDate && !updatedDuration) {
			const newShopper: Partial<Shopper> = {
				firstName: newFirstName,
				lastName: newLastName,
				segment: newSegment,
				userProperties: newUserProperties,
			};
			const updatedRentalInfo: Partial<OrderInfo> = {
				shopperNameKeywords: getShopperNameKeywords(
					getUpdatedShoppers(),
					rentalInfo.responsiblePerson,
				),
				shopperNames: getShopperNames(getUpdatedShoppers(), rentalInfo.responsiblePerson),
			};
			const updatedShopperProducts: PartialWithId<OrderProduct>[] = shopperProducts
				.filter((p) => p.collectDin || p.setProducts.some((setP) => setP.collectDin))
				.map((p) => {
					const newDinValues = getUpdatedProductDinValues(p, {
						userProperties: newUserProperties,
						t,
						dinCollectionMethod: shopDinCollectionMethod,
					});
					if (!newDinValues) return undefined;
					return {
						id: p.id,
						data: newDinValues,
					};
				})
				.filter(notUndefined);
			dispatch(
				updateActivePartialOrder({
					shoppers: [
						{
							id: shopper.id,
							data: newShopper,
						},
					],
					rentalInfo: {
						id: rentalInfo.id,
						data: updatedRentalInfo,
					},
					products: updatedShopperProducts,
				}),
			);
			props.endEditing();
			return;
		}

		// If segment or duration changes
		const updatedShoppers = getUpdatedShoppers();
		let allProductsToUpdate = products;
		let updatedRentalInfo: Partial<OrderInfo> | undefined;

		if (updatedShopperProductsOnSegmentChange.length) {
			const otherShoppersRentalProducts = products.filter(
				(product) => product.shopperId !== shopper.id,
			);
			allProductsToUpdate = [
				...otherShoppersRentalProducts,
				...updatedShopperProductsOnSegmentChange,
			];
		}

		// check for possible duration change
		if (updatedDuration || updatedEndDate) {
			const startDate = getEarliestProductStartTime(shopperProducts);
			const newEndDate =
				updatedEndDate ||
				(startDate && newRentalDuration
					? moment(startDate).add(newRentalDuration.durationInSeconds, 'seconds').toISOString()
					: null);

			// updating products
			const updatedShopper = updatedShoppers.find((s) => s.id === shopper.id);
			const segment = updatedShopper ? updatedShopper.segment : undefined;
			let updatedShopperProducts = allProductsToUpdate.filter(
				(p) => p.shopperId === shopper.id && !p.endDateReturned,
			);
			updatedShopperProducts = shopperProducts.map((p) => {
				const newDinValues = getUpdatedProductDinValues(p, {
					userProperties: newUserProperties,
					t,
					dinCollectionMethod: shopDinCollectionMethod,
				});
				if (!newDinValues) return p;
				return {
					...p,
					...newDinValues,
				};
			});
			let updatedShopperProductsByDate = updateOrderProductsDates(
				updatedShopperProducts,
				startDate,
				newEndDate,
			);
			if (updatedDuration) {
				updatedShopperProductsByDate = updateOrderProductsDuration(
					updatedShopperProductsByDate,
					updatedDuration,
				);
			}
			updatedShopperProductsByDate = updateOrderProductsPricing(
				updatedShopperProductsByDate,
				stockProducts,
				shopPublicInfo.pricingTables || {},
				'ADMIN',
				segment,
			);

			const otherShoppersRentalProducts = products.filter(
				(p) => p.shopperId !== shopper.id || p.endDateReturned,
			);
			allProductsToUpdate = [...otherShoppersRentalProducts, ...updatedShopperProductsByDate];

			// updating return time
			const notReturnedItems = products.filter(
				(product) =>
					!updatedShopperProductsByDate.find(
						(updatedProduct) => updatedProduct.id === product.id,
					) && product.endDateReturned === null,
			);
			const returnTimeUpdatedProducts = getNextProductReturnTime(updatedShopperProductsByDate);
			let returnTimeNext = getNextProductReturnTime(updatedShopperProductsByDate);
			if (notReturnedItems.length > 0) {
				const returnTimeNotReturnedItems = getNextProductReturnTime(notReturnedItems);
				returnTimeNext = moment(returnTimeUpdatedProducts!).isSameOrBefore(
					moment(returnTimeNotReturnedItems!),
				)
					? returnTimeUpdatedProducts
					: returnTimeNotReturnedItems;
			}
			const newOrderPricing = getOrderPricing(
				allProductsToUpdate,
				orderDelivery,
				getPricingOptions(rentalInfo.pricing),
			);
			updatedRentalInfo = {
				returnTimeNext,
				categoryIds: getCategoryIdsFromShoppers(updatedShoppers),
				stockProductIds: getStockProductIdsFromProducts(allProductsToUpdate),
				shopperIdsWithProducts: getShopperIdsWithProductsFromShoppers(updatedShoppers),
				shopperNameKeywords: getShopperNameKeywords(updatedShoppers, rentalInfo.responsiblePerson),
				shopperNames: getShopperNames(updatedShoppers, rentalInfo.responsiblePerson),
				pricing: newOrderPricing,
			};
		} else {
			const newOrderPricing = getOrderPricing(
				allProductsToUpdate,
				orderDelivery,
				getPricingOptions(rentalInfo.pricing),
			);
			updatedRentalInfo = {
				pricing: newOrderPricing,
			};
		}
		dispatch(
			updateActivePartialOrder({
				shoppers: updatedShoppers.map((s) => ({
					id: s.id,
					data: s,
				})),
				products: allProductsToUpdate.map((p) => ({
					id: p.id,
					data: p,
				})),
				...(updatedRentalInfo && {
					rentalInfo: {
						id: rentalInfo.id,
						data: updatedRentalInfo,
					},
				}),
			}),
		);
		setUpdatedShopperProductsOnSegmentChange([]);
		props.endEditing();
	};

	const renderShopperDurationOptions = (shopPublicInfo: ShopPublicInfo) => {
		const productsForShopper = updatedShopperProductsOnSegmentChange.length
			? updatedShopperProductsOnSegmentChange
			: shopperProducts;

		const productsNotYetReturned = productsForShopper.filter((product) => !product.endDateReturned);
		const totalCurrentListPrice = getTotalProductPricing(productsNotYetReturned).listPrice;
		const segment = newSegment ? newSegment : shopper.segment;
		const durationOptions = getDurationOptionsForOrderProducts(
			productsNotYetReturned,
			stockProducts,
			shopPublicInfo.pricingTables || {},
			openingHoursWithExceptions,
			rentalInfo.startDate,
			'ADMIN',
			segment,
		);

		return durationOptions.length > 0 ? (
			<DurationDropdownWithAvailability
				value={getSelectedDurationOptionIndex(durationOptions, newRentalDuration || rentalDuration)}
				onValueChange={handleRentalDurationChange}
				options={durationOptions}
				showPriceChange={true}
				currentPrice={totalCurrentListPrice}
				disabled={rentalHasPickup}
				productsNotYetReturned={productsNotYetReturned}
				loading={durationProductAvailabilities.loading}
				productAvailabilities={durationProductAvailabilities.data}
				openingHours={openingHoursWithExceptions}
				useAvailabilities={useAvailabilities}
			/>
		) : (
			<TextField
				label={t('shopperCard.durationAndPricing', 'Duration and pricing')}
				value={t(
					'timeLocationSelector.noDurationOptionsAvailable',
					'No duration options available.',
				)}
				disabled
				fullWidth
				variant="outlined"
			/>
		);
	};

	return (
		<React.Fragment>
			<EditHeaderBar
				onCancel={handleUndoEditingShopper}
				onSave={handleSaveShopperChanges}
				savingDisabled={isFormError || (!allowOverbookings && showBanner)}
			/>
			<Collapse in={showBanner}>
				<Box mb={2} />
				<OverbookedBanner
					allowOverbookings={allowOverbookings}
					rentalProducts={shopperProducts}
					overbookedVariantIds={overbookedVariantIds}
					openingHours={{
						isOutsideOpeningHours: isOutsideOpeningHours,
					}}
				/>
			</Collapse>
			<Box mb={2} />
			<Grid container justifyContent="flex-start" spacing={1} className={classes.spacingBottom}>
				<Grid item xs={6}>
					<TextField
						label={t('common:form.firstName')}
						value={newFirstName}
						onChange={handleNewFirstNameChange}
						fullWidth
						variant="outlined"
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						label={t('common:form.lastName')}
						value={newLastName}
						onChange={handleNewLastNameChange}
						fullWidth
						variant="outlined"
					/>
				</Grid>
				{shopperHasRentals && (
					<>
						<Grid item xs={12}>
							{renderShopperDurationOptions(shopPublicInfo)}
						</Grid>
						<Grid item xs={12}>
							<DateAndTimeSelector
								date={newEndDate}
								selectorType={selectorType}
								setDate={(newDate) => {
									setNewEndDate(newDate.toISOString());
									setTimeEditHasHappened(true);
								}}
								label={t('timeLocationSelector.selectEndDate', 'Select end date')}
								variant="outlined"
								disabled={rentalHasPickup}
								orderProducts={shopperProducts}
								startLocationId={rentalInfo.startLocation.id}
								endLocationId={rentalInfo.endLocation.id}
								duration={rentalDuration}
								styling={{ disableUnavailableTimeSlots: true, showOverbookingBanner: false }}
							/>
						</Grid>
					</>
				)}
				<HasFeature requiredFeature="SEGMENTS">
					<Grid item xs={12}>
						<SegmentSelect
							segmentOptions={segmentOptions}
							value={newSegment}
							onSegmentChange={handleNewSegmentChange}
							currency={currency}
						/>
					</Grid>
				</HasFeature>
			</Grid>
			<Grid container justifyContent="flex-start" spacing={1}>
				{details.map((detail, index) => (
					<Grid item xs={6} key={detail + '-' + index}>
						<UserDetailField
							detail={detail}
							unitSystem={shopUnitSystem}
							userProperty={newUserProperties[detail]}
							onChange={(newValue) => handleNewUserPropertiesChange(newValue, detail)}
							t={t}
						/>
					</Grid>
				))}
			</Grid>
		</React.Fragment>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		flexGrow: {
			flexGrow: 1,
		},
		spacingBottom: {
			paddingBottom: theme.spacing(2),
		},
		optionWrapper: {
			width: '100%',
			display: 'flex',
			justifyContent: 'space-between',
			lineHeight: 1,
		},
		primaryOption: {
			flex: 1,
		},
		secondaryOption: {
			textAlign: 'right',
			fontWeight: 400,
		},
	}),
);

export default EditView;
